// Étape 17 — Objectifs personnels du consultant (tableau de bord)
import { useState, useEffect } from "react";
import axios from "axios";
import { Trophy } from "lucide-react";
import { C, Btn, Input, Modal, fmtEur, errMsg } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } });
const mois = () => new Date().toISOString().slice(0, 7);
const fmt = (t, v) => (t === "encaisse" ? fmtEur(v) : v);

const FixerModal = ({ data, onClose, onSaved }) => {
  const init = Object.fromEntries(Object.keys(data.types).map(t => [t, (data.items.find(i => i.type === t) || {}).cible || ""]));
  const [v, setV] = useState(init);
  const [on, setOn] = useState(Object.fromEntries(Object.keys(data.types).map(t => [t, data.items.some(i => i.type === t)])));
  const [err, setErr] = useState("");
  const items = Object.keys(on).filter(t => on[t]).map(t => ({ type: t, cible: Number(v[t]) }));
  const ok = items.length >= 1 && items.length <= 4 && items.every(i => i.cible > 0);
  const save = async () => { try { await axios.put(`${API}/objectifs`, { mois: mois(), items }, hdr()); onSaved(); onClose(); } catch (e) { setErr(errMsg(e)); } };
  return (
    <Modal title="Fixer mes objectifs du mois" onClose={onClose}
      footer={<><Btn onClick={onClose}>Annuler</Btn><Btn variant="gold" disabled={!ok} onClick={save} data-testid="objectifs-save">Enregistrer</Btn></>}>
      <div style={{ fontSize: 12, color: C.mid, marginBottom: 12 }}>Choisissez de 1 à 4 objectifs. Ils sont reconduits le mois suivant, modifiables à tout moment.</div>
      {err && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {Object.entries(data.types).map(([t, label]) => (
        <div key={t} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", flex: 1, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={on[t]} onChange={(e) => setOn({ ...on, [t]: e.target.checked })} style={{ width: 16, height: 16, accentColor: C.gold }} data-testid={`objectif-check-${t}`} />{label}
          </label>
          <Input type="number" min={1} value={v[t]} disabled={!on[t]} onChange={(e) => setV({ ...v, [t]: e.target.value })} style={{ width: 120 }} placeholder={t === "encaisse" ? "€" : "nombre"} data-testid={`objectif-cible-${t}`} />
        </div>
      ))}
    </Modal>
  );
};

const Bravo = ({ items, onClose }) => (
  <Modal title="Félicitations !" onClose={onClose} footer={<Btn variant="gold" onClick={onClose} data-testid="bravo-close">Merci !</Btn>}>
    <div data-testid="bravo-modal" style={{ textAlign: "center", padding: "6px 0 4px" }}>
      <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.goldlt, border: `2px solid ${C.gold}`, display: "grid", placeItems: "center", margin: "0 auto 14px", animation: "bravoPop .5s ease-out" }}>
        <Trophy size={34} color={C.golddm} />
      </div>
      <style>{"@keyframes bravoPop{0%{transform:scale(.4);opacity:0}70%{transform:scale(1.1)}100%{transform:scale(1);opacity:1}}"}</style>
      <div style={{ fontSize: 15, color: C.navy, fontWeight: 700, marginBottom: 8 }}>{items.length > 1 ? "Vous avez atteint vos objectifs du mois :" : "Vous avez atteint votre objectif du mois :"}</div>
      {items.map(o => <div key={o.type} style={{ fontSize: 13, color: C.mid }} data-testid={`bravo-${o.type}`}>{o.label} — {fmt(o.type, o.realise)} / {fmt(o.type, o.cible)}</div>)}
      <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>Bravo, continuez sur cette lancée !</div>
    </div>
  </Modal>
);

const Badge100 = ({ type }) => (
  <span data-testid={`objectif-badge-${type}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: C.golddm, background: C.goldlt, border: `1px solid ${C.gold}`, borderRadius: 10, padding: "1px 7px", marginLeft: 6 }}>
    <Trophy size={11} /> Objectif atteint
  </span>
);

const Classement = ({ reload }) => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/objectifs/classement`, { ...hdr(), params: { mois: mois() } }).then(r => setD(r.data)).catch(() => {}); }, [reload]);
  if (!d || !d.participants) return null;
  const moi = d.lignes.find(l => l.vous);
  const top = d.lignes.slice(0, 5);
  const rows = moi && !top.includes(moi) ? [...top, null, moi] : top;
  return (
    <div data-testid="objectifs-classement" style={{ marginTop: 16, borderTop: `.5px solid ${C.border}`, paddingTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.navy }}>Classement du réseau</div>
        <div style={{ fontSize: 11, color: C.muted }} data-testid="classement-resume">{moi ? `Vous êtes ${moi.rang}${moi.rang === 1 ? "er" : "e"} sur ${d.participants}` : `${d.participants} participant(s)`} · anonyme</div>
      </div>
      {rows.map((l, i) => l === null ? <div key="sep" style={{ textAlign: "center", color: C.muted, fontSize: 11 }}>⋯</div> : (
        <div key={i} data-testid={l.vous ? "classement-vous" : `classement-ligne-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 8px", borderRadius: 8, background: l.vous ? C.goldlt : "transparent", fontWeight: l.vous ? 700 : 400 }}>
          <div style={{ width: 26, fontSize: 12, color: l.rang <= 3 ? C.golddm : C.mid, fontWeight: 700, display: "flex", alignItems: "center", gap: 2 }}>{l.rang === 1 && <Trophy size={12} />}{l.rang}</div>
          <div style={{ width: 110, fontSize: 12, color: C.navy }}>{l.libelle}</div>
          <div style={{ flex: 1, height: 6, background: C.gray2, borderRadius: 4, overflow: "hidden" }}><div style={{ width: `${l.taux}%`, height: "100%", background: l.vous ? C.gold : C.navy2 || C.navy }} /></div>
          <div style={{ width: 120, fontSize: 11, color: C.mid, textAlign: "right" }}>{l.taux} % · {l.atteints}/{l.objectifs} atteint{l.atteints > 1 ? "s" : ""}</div>
        </div>
      ))}
    </div>
  );
};

export const MesObjectifs = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [bravo, setBravo] = useState([]);
  const load = () => axios.get(`${API}/objectifs`, { ...hdr(), params: { mois: mois() } }).then(r => { setData(r.data); setBravo(r.data.items.filter(i => i.a_feliciter)); }).catch(() => {});
  const closeBravo = () => { axios.post(`${API}/objectifs/vus`, { mois: mois(), types: bravo.map(b => b.type) }, hdr()).catch(() => {}); setBravo([]); };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  return (
    <div data-testid="mes-objectifs" style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>Mes objectifs du mois</div>
          {data.reconduit && <div style={{ fontSize: 11, color: C.muted }}>Reconduits du mois précédent</div>}
        </div>
        <Btn small variant="gold" onClick={() => setOpen(true)} data-testid="fixer-objectifs">Fixer mes objectifs</Btn>
      </div>
      {!data.items.length && <div style={{ fontSize: 12, color: C.muted }}>Aucun objectif fixé. Choisissez jusqu'à 4 objectifs pour suivre votre progression.</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        {data.items.map(o => (
          <div key={o.type} data-testid={`objectif-${o.type}`}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: C.mid }}>{o.label}{o.atteint && <Badge100 type={o.type} />}</span><strong style={{ color: C.navy }}>{fmt(o.type, o.realise)} / {fmt(o.type, o.cible)}</strong>
            </div>
            <div style={{ height: 8, background: C.gray2, borderRadius: 6, overflow: "hidden" }}>
              <div style={{ width: `${o.pct}%`, height: "100%", background: o.pct >= 100 ? C.green : C.gold, transition: "width .4s" }} />
            </div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{o.pct} %</div>
          </div>
        ))}
      </div>
      <Classement reload={data} />
      {open && <FixerModal data={data} onClose={() => setOpen(false)} onSaved={load} />}
      {bravo.length > 0 && !open && <Bravo items={bravo} onClose={closeBravo} />}
    </div>
  );
};
